import { useEffect, useRef, useState } from "react";

import { Box, Image } from "@chakra-ui/react";
import {
  IIndividualFlexSavingsPayload,
  TSavingsFrequency,
  TSavingsStatus,
} from "api/individual-savings/types";
import { useIndividualsSavings } from "contexts/individuals-savings-context";
import {
  DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS,
  FIRST_INDEX,
  flexSavingsColumns,
} from "features/individuals-savings/consts";
import { EmptyImagePlaceholder } from "features/individuals/individuals/individuals-list-table/EmptyImagePlaceholder";
import { individualTableColumnCss } from "features/individuals/individuals/styles";
import { useTableActions } from "hooks/useTableActions";
import { useSearchParams } from "react-router-dom";
import {
  capitalizeFirstLetter,
  capitalizeName,
  formatDateToDDMMMYYYY,
  handleAmountFormat,
} from "utils";
import { ONE_NAIRA_IN_KOBO } from "utils/constants";

import Pagination, { DEFAULT_PAGINATION_DATA } from "ui/pagination";
import { Status } from "ui/status";
import { TStatus } from "ui/status/types";
import Table from "ui/table";
import TableWrapper from "ui/table/table-wrapper";
import { TSelectedTableRowIds } from "ui/table/types";

import { IndividualsSavingsStats } from "./IndividualsSavingsStats";
import { TableHeaderCTAs } from "./TableHeaderCTAs";
import { MoreQuickActions } from "./more-quick-actions";

export const getTransationStatus = (status: string) => {
  let Savingstatus = status;

  if (Savingstatus === "active") {
    Savingstatus = "success";
  } else if (Savingstatus === "paused" || Savingstatus === "stopped") {
    Savingstatus = "error";
  } else if (
    Savingstatus === "completed" ||
    Savingstatus === "disbursed" ||
    Savingstatus === "matured"
  ) {
    Savingstatus = "blue";
  } else {
    Savingstatus = "disabled";
  }

  return Savingstatus;
};

export const getTransationStatusText = (status: string) => {
  let Savingstatus = status;

  if (Savingstatus === "active") {
    Savingstatus = "Active";
  } else if (
    Savingstatus === "completed" ||
    Savingstatus === "disbursed" ||
    Savingstatus === "matured"
  ) {
    Savingstatus = "Completed";
  } else if (Savingstatus === "deleted" || Savingstatus === "stopped") {
    Savingstatus = "Stopped";
  } else {
    Savingstatus = capitalizeFirstLetter(Savingstatus);
  }

  return Savingstatus;
};

const FlexSavings = () => {
  const {
    isLoadingIndividualFlexSavings,
    handleGetIndividualFlexSavings,
    individualFlexSavingsResult,
    isLoadingIndividualFlexSavingsStats,
    handleGetIndividualFlexSavingsStats,
  } = useIndividualsSavings();
  const { handleRefreshTable, isRefreshing } = useTableActions({
    isLoadingTable: isLoadingIndividualFlexSavings,
    onTableRefresh: () => {
      void handleGetIndividualFlexSavingsStats(
        {},
        {
          isRefetching: true,
        }
      );
    },
  });

  const [selectedSavingsIds, setSelectedSavingsIds] =
    useState<TSelectedTableRowIds>({});

  const tableRef = useRef<HTMLTableElement | null>(null);
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page");
  const limit = searchParams.get("limit");
  const status = searchParams.get("status");
  const frequency = searchParams.get("frequency");
  const search = searchParams.get("search");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const tableRefreshCount = searchParams.get("tableRefreshCount");
  const filterRetryCount = searchParams.get("filterRetryCount");

  useEffect(() => {
    const payload: IIndividualFlexSavingsPayload = {
      page: String(page || DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS.page),
      limit: String(
        limit || DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS.limit
      ),
    };

    if (startDate) {
      payload["startDate"] = startDate;
    }

    if (endDate) {
      payload["endDate"] = endDate;
    }

    if (frequency) {
      const transactionFrequency = frequency.split(",");
      const filteredFrequency = transactionFrequency
        .filter((freq) => freq !== "all")
        .join(",");
      payload["frequency"] = filteredFrequency as TSavingsFrequency;
    }

    if (status) {
      const flexSavingsStatuses = status.split(",");
      if (!flexSavingsStatuses.includes("all")) {
        payload["status"] = flexSavingsStatuses.join(",") as TSavingsStatus;
      }
    }

    if (search) {
      payload["search"] = search;
      payload["searchBy"] = "firstName,lastName,email";
    }

    void handleGetIndividualFlexSavings(payload);
  }, [
    page,
    limit,
    startDate,
    endDate,
    frequency,
    status,
    filterRetryCount,
    search,
    tableRefreshCount,
  ]);

  const { data, ...paginationData } = individualFlexSavingsResult || {
    data: [],
    ...DEFAULT_PAGINATION_DATA,
  };
  const formattedData = data?.map((saving) => {
    const {
      id,
      frequency,
      email,
      firstName,
      lastName,
      halal: halalStatus,
      flexInterest: interestEarned,
      balance: savedAmount,
      createdAt: startDate,
      source,
    } = saving;

    const fullName =
      firstName && lastName && `${capitalizeName(firstName, lastName)}`;

    return {
      id,
      "Saver's Name": (
        <Box className="flex gap-3 items-center">
          {!id ? (
            <Image
              className="w-[32px] h-[32px] rounded-[50%] border border-[#E5E5E5] flex-shrink-0"
              src={"profileImage"}
              alt={`${fullName}'s image`}
            />
          ) : (
            <EmptyImagePlaceholder
              className="w-[32px] h-[32px] uppercase text-xs font-semibold rounded-[50%] border border-[#E5E5E5] flex-shrink-0"
              initial={fullName?.[FIRST_INDEX]}
              index={id as string}
            />
          )}
          <Box css={individualTableColumnCss}>
            <Box className="text-sm leading-[14px] tracking-[0%] mb-1 font-semibold text-[#09090B]">
              {fullName || "N/A"}
            </Box>
            <Box className="font-normal tracking-[0%] text-xs leading-[12px] text-[#718096]">
              {email || "-"}
            </Box>
          </Box>
        </Box>
      ),
      "Amount Saved": (
        <Box css={individualTableColumnCss}>
          {savedAmount === null
            ? "N/A"
            : handleAmountFormat({
                value: String(savedAmount / ONE_NAIRA_IN_KOBO),
                includeNairaSymbol: true,
                shouldFormatZero: true,
              })}
        </Box>
      ),
      "Interest Earned": (
        <Box css={individualTableColumnCss}>
          {interestEarned === null
            ? "N/A"
            : handleAmountFormat({
                value: String(interestEarned / ONE_NAIRA_IN_KOBO),
                includeNairaSymbol: true,
                shouldFormatZero: true,
              })}
        </Box>
      ),
      "Start Date": (
        <Box css={individualTableColumnCss}>
          {startDate ? formatDateToDDMMMYYYY(startDate) : "N/A"}
        </Box>
      ),
      Source: (
        <Box css={individualTableColumnCss}>
          {(source && (source === "wallet" ? "Account" : "Card")) || "N/A"}
        </Box>
      ),
      "Zero Interest": (
        <Status
          status={
            halalStatus
              ? (getTransationStatus("active") as TStatus)
              : (getTransationStatus("disabled") as TStatus)
          }
          text={halalStatus ? "On" : "Off"}
          className="w-fit"
        />
      ),
      Frequency: (
        <Box css={individualTableColumnCss}>
          {frequency ? capitalizeFirstLetter(frequency) : "N/A"}
        </Box>
      ),
      "": <MoreQuickActions individualsSavingDetails={saving} />,
    };
  });

  return (
    <div>
      <Box>
        <IndividualsSavingsStats
          isLoadingIndividualsStats={isLoadingIndividualFlexSavingsStats}
        />
      </Box>
      <TableWrapper className="!p-0">
        <TableHeaderCTAs
          handleGetTransactions={handleGetIndividualFlexSavings}
          isLoadingTransactions={isLoadingIndividualFlexSavings}
          isRefreshing={isRefreshing}
          handleRefreshTable={handleRefreshTable}
        />
        <>
          <Table
            columns={flexSavingsColumns}
            data={formattedData || []}
            tableRef={tableRef}
            hasCheckbox
            isLoading={isLoadingIndividualFlexSavings && !isRefreshing}
            selectedTableRowIds={selectedSavingsIds}
            setSelectedTableRowIds={setSelectedSavingsIds}
          />
          <Pagination
            paginationData={paginationData}
            data={data}
            defaultPaginationParams={
              DEFAULT_SAVINGS_TRANSACTIONS_PAGINATION_PARAMS
            }
            tableRef={tableRef}
            isLoadingTable={isLoadingIndividualFlexSavings}
          />
        </>
      </TableWrapper>
    </div>
  );
};

export default FlexSavings;
